import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

import MDBox from 'components/MDBox';
import DataTable from '../../../examples/Tables/DataTable';

import {
  getUserSavedProperties,
  resetPropertiesList,
  getUserInvestedProperties,
} from 'store/reducers/usersSlice';
import MDTypography from 'components/MDTypography';
import { formatCurrency } from 'helpers/formatData';
import {
  investedPropertiesColumns,
  savedPropertiesColumns,
} from 'constants/columns.constants';

const Properties = ({ activeTab }) => {
  const dispatch = useDispatch();
  const { userId } = useParams();

  const { propertiesList } = useSelector((state) => state.usersSlice);

  const [isLoading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });

  const columns = useMemo(() => {
    switch (activeTab) {
      case 'invested':
        return investedPropertiesColumns;

      case 'saved':
        return savedPropertiesColumns;

      default:
        return [];
    }
  }, [activeTab]);

  const rows = useMemo(() => {
    switch (activeTab) {
      case 'invested':
        return propertiesList?.propertiesData?.length > 0
          ? propertiesList?.propertiesData?.map((property) => {
              return {
                id: property?.propertyId,
                name: (
                  <MDTypography
                    fontSize="14px"
                    color="text"
                    sx={{
                      maxWidth: '20ch',
                    }}
                  >
                    {property?.propertyName}
                  </MDTypography>
                ),
                shares: property?.noOfSharesPurchased,
                amountInvested: formatCurrency(property?.amountInvested),
                totalDividendReceived: formatCurrency(
                  property?.totalDividendReceived
                ),
              };
            })
          : [];

      case 'saved':
        return propertiesList?.propertiesData?.length > 0
          ? propertiesList?.propertiesData?.map((property) => {
              return {
                id: property?.propertyId,
                propertyName: (
                  <MDTypography
                    fontSize="14px"
                    color="text"
                    sx={{
                      maxWidth: '20ch',
                    }}
                  >
                    {property?.propertyName}
                  </MDTypography>
                ),
                propertyType: (
                  <MDTypography
                    fontSize="14px"
                    color="text"
                    sx={{
                      maxWidth: '80ch',
                    }}
                  >
                    {property?.propertyType}
                  </MDTypography>
                ),
              };
            })
          : [];

      default:
        return [];
    }
  }, [activeTab, propertiesList?.propertiesData]);

  async function fetchUserProperties() {
    try {
      setLoading(true);
      switch (activeTab) {
        case 'invested':
          await dispatch(
            getUserInvestedProperties({
              userId,
              page: currentPage + 1,
              limit: perPage,
              sortField: sortBy?.id ?? 'id',
              sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
              // search: searchTerm, // TODO
            })
          );
          break;

        case 'saved':
          await dispatch(
            getUserSavedProperties({
              userId,
              page: currentPage + 1,
              limit: perPage,
              sortField: sortBy?.id ?? 'id',
              sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
              // search: searchTerm, // TODO
            })
          );
          break;

        default:
          break;
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onFetchDataDebounced = debounce(fetchUserProperties, 0);

  useEffect(() => {
    onFetchDataDebounced();
    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [userId, activeTab, currentPage, sortBy, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetPropertiesList());
    };
  }, []);

  return (
    <MDBox
      mt={3}
      sx={{
        position: 'relative',
        paddingTop: '85px',
        paddingBottom: {
          xs: '150px',
          sm: '95px',
        },
      }}
    >
      <DataTable
        table={{ columns, rows: rows }}
        isSorted={true}
        entriesPerPage={{ defaultValue: perPage }}
        showTotalEntries
        canSearch={false}
        // searchTerm={searchTerm}
        // setSearchTerm={setSearchTerm}
        perPage={perPage}
        setPerPage={setPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={propertiesList?.totalPage}
        totalRecords={propertiesList?.totalCount}
        hiddenColumns={['id']}
        queryPageSortBy={[sortBy]}
        setSortBy={setSortBy}
        isLoading={isLoading}
      />
    </MDBox>
  );
};

Properties.propTypes = {
  activeTab: PropTypes.string.isRequired,
};

export default Properties;
